import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { GoogleMap, MarkerF, PolylineF, InfoWindowF, useJsApiLoader } from "@react-google-maps/api";
import backend from "../../../../../api";
import AppHeader from "../../../../../components/AppHeader";
import "./UserMap.css";
import { useSelector } from "react-redux";
import { getSocket, safeEmit } from "../../../../../api/socket";

export default function GroupMap() {
  const { groupId } = useParams();
  const nav = useNavigate();
  const { user } = useSelector(s => s.auth);
  const userId = user?._id;

  const [group, setGroup] = useState(null);
  const [locations, setLocations] = useState({});
  const [err, setErr] = useState("");
  const [activeInfo, setActiveInfo] = useState(null);
  const [activePath, setActivePath] = useState(null);
  const [sharing, setSharing] = useState(false);

  // request-delivery retry state
  const reqTargetRef = useRef(null);
  const reqAttemptsRef = useRef(0);
  const reqTimerRef = useRef(null);

  const watchRef = useRef(null);
  const mapRef = useRef(null);

  const { isLoaded, loadError } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: "AIzaSyAOGi6Lrm0NrvFD6iuX7odfmhl_SM7qY8U"
  });

  const triggerMapResize = useCallback(() => {
    if (mapRef.current && window.google) {
      window.google.maps.event.trigger(mapRef.current, "resize");
    }
  }, []);
  useEffect(() => {
    window.addEventListener("resize", triggerMapResize);
    return () => window.removeEventListener("resize", triggerMapResize);
  }, [triggerMapResize]);

  const loadGroup = async () => {
    try {
      setErr("");
      const res = await backend.get(`/groups/${groupId}`);
      setGroup(res?.data?.data || null);
    } catch (e) {
      setErr(e?.response?.data?.message || "Failed to load group");
    }
  };
  useEffect(() => { loadGroup(); }, [groupId]);

  // prime locations + subscribe to each member
  useEffect(() => {
    if (!group?.members) return;
    const socket = getSocket();
    if (!socket) return;

    group.members.forEach(async (m) => {
      const mid = m._id;
      try {
        const res = await backend.get(`/location/${mid}`);
        setLocations(prev => ({ ...prev, [mid]: res?.data?.data || null }));
      } catch {}

      socket.emit("join-room", { roomId: `user:${mid}` });
    });

    const onUpdate = (data) => {
      setLocations(prev => {
        const old = prev[data.userId] || {};
        const newHist = old.history ? [...old.history] : [];
        if (old.lat && (old.lat !== data.lat || old.lng !== data.lng)) {
          newHist.push({ lat: old.lat, lng: old.lng, accuracy: old.accuracy, ts: old.updatedAt || new Date() });
        }
        return { ...prev, [data.userId]: { ...data, history: newHist } };
      });
    };

    socket.on("location:updated", onUpdate);

    return () => {
      group.members.forEach(m => socket.emit("leave-room", { roomId: `user:${m._id}` }));
      socket.off("location:updated", onUpdate);
    };
  }, [group]);

  // ack + retry for location request
  useEffect(() => {
    const s = getSocket();
    if (!s) return;

    const onDelivered = (data) => {
      // { to, delivered } ack comes back to requester
      if (!data || !reqTargetRef.current) return;
      if (String(data.to) !== String(reqTargetRef.current)) return;

      if (data.delivered) {
        // Stop retrying; the receiver will show a popup (via IncomingLocationListener)
        clearTimeout(reqTimerRef.current); reqTimerRef.current = null;
        reqAttemptsRef.current = 0;
      } else {
        // schedule another try
        clearTimeout(reqTimerRef.current);
        reqAttemptsRef.current += 1;
        if (reqAttemptsRef.current > 8) {
          reqAttemptsRef.current = 0;
          return;
        }
        reqTimerRef.current = setTimeout(() => {
          if (!reqTargetRef.current) return;
          safeEmit("location:request", { to: reqTargetRef.current, from: userId, fromName: user?.name }).catch(()=>{});
        }, 1200);
      }
    };

    s.on("location:request-delivered", onDelivered);
    return () => {
      s.off("location:request-delivered", onDelivered);
      if (reqTimerRef.current) { clearTimeout(reqTimerRef.current); reqTimerRef.current = null; }
    };
  }, [userId, user?.name]);

  const acceptLocationOnce = async () => {
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude: lat, longitude: lng, accuracy } = pos.coords;
        try { await backend.post("/location", { lat, lng, accuracy }, { params: { userId } }); } catch {}
      },
      () => {},
      { enableHighAccuracy: true }
    );
  };

  const onMapLoad = useCallback((map) => { mapRef.current = map; triggerMapResize(); }, [triggerMapResize]);

  const center = useMemo(() => {
    const poss = Object.values(locations).filter(l => l?.lat);
    if (!poss.length) return { lat: 24.7136, lng: 46.6753 };
    const avgLat = poss.reduce((s, p) => s + p.lat, 0) / poss.length;
    const avgLng = poss.reduce((s, p) => s + p.lng, 0) / poss.length;
    return { lat: avgLat, lng: avgLng };
  }, [locations]);

  const fitToPath = (path) => {
    if (!mapRef.current || !window.google || !Array.isArray(path) || !path.length) return;
    const bounds = new window.google.maps.LatLngBounds();
    path.forEach(p => bounds.extend(p));
    mapRef.current.fitBounds(bounds, { top: 60, right: 40, bottom: 40, left: 40 });
    triggerMapResize();
  };

  const onUserClick = (m) => {
    const loc = locations[m._id];
    if (!loc || !loc.lat) return;
    const path = (loc.history || []).map(p => ({ lat: p.lat, lng: p.lng })).concat({ lat: loc.lat, lng: loc.lng });
    setActivePath(path);
    fitToPath(path);
    setActiveInfo({ id: m._id, name: m.name, pos: { lat: loc.lat, lng: loc.lng } });
  };

  const onClearTrack = () => { setActivePath(null); setActiveInfo(null); };

  const requestLocation = async (mid) => {
    reqTargetRef.current = mid;
    reqAttemptsRef.current = 0;
    // fire first attempt; server will ack (delivered true/false)
    await safeEmit("location:request", { to: mid, from: userId, fromName: user?.name });
  };

  const toggleSharing = () => {
    if (sharing) {
      if (watchRef.current) navigator.geolocation.clearWatch(watchRef.current);
      watchRef.current = null;
      setSharing(false);
      return;
    }
    // share once immediately and then watch
    acceptLocationOnce();
    watchRef.current = navigator.geolocation.watchPosition(
      async (pos) => {
        const { latitude: lat, longitude: lng, accuracy } = pos.coords;
        try { await backend.post("/location", { lat, lng, accuracy }, { params: { userId } }); } catch {}
      },
      () => {},
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
    setSharing(true);
  };

  return (
    <div className="um-page">
      <AppHeader
        title="Group map"
        onBack={() => nav(-1)}
        right={
          <div className="um-actions">
            <button className="um-btn" onClick={loadGroup}>Refresh</button>
            <button className="um-btn" onClick={toggleSharing}>{sharing ? "Stop" : "Start"} sharing</button>
            <button className="um-btn outline" onClick={onClearTrack}>Clear track</button>
          </div>
        }
      />

      {err && <div className="um-error">{err}</div>}
      {loadError && <div className="um-error">Map failed to load. Check API key.</div>}

      <div className="um-layout">
        <aside className="um-sidebar">
          <div className="um-card">
            <div className="um-subtitle">Group members</div>
            {group?.members?.map(m => (
              <div key={m._id} className="um-userRow">
                <div className="um-dot brand" />
                <div className="um-userName" onClick={() => onUserClick(m)} role="button">
                  {m.name || m._id}
                </div>
                <div className="um-meta">{locations[m._id]?.lat ? "Live" : "No location"}</div>
                {!locations[m._id]?.lat && m._id !== userId && (
                  <button className="um-btn small" onClick={() => requestLocation(m._id)}>Request</button>
                )}
              </div>
            ))}
          </div>

          <div className="um-card">
            <div className="um-subtitle">Legend</div>
            <div className="um-legendRow"><span className="um-line" /><span>Route history</span></div>
            <div className="um-legendRow"><span className="um-dot brand" /><span>User location</span></div>
          </div>
        </aside>

        <div className="um-mapWrap">
          {isLoaded ? (
            <GoogleMap
              zoom={13}
              center={center}
              onLoad={onMapLoad}
              mapContainerClassName="um-mapCanvas"
              options={{ disableDefaultUI: false, clickableIcons: false, mapTypeControl: false, streetViewControl: false }}
            >
              {Object.entries(locations).map(([id, loc]) => loc?.lat && (
                <MarkerF
                  key={id}
                  position={{ lat: loc.lat, lng: loc.lng }}
                  onClick={() => onUserClick(group.members.find(m => String(m._id) === String(id)))}
                  label={(group.members.find(m => String(m._id) === String(id))?.name || "U").slice(0,1)}
                  title="User location"
                />
              ))}

              {activeInfo && (
                <InfoWindowF position={activeInfo.pos} onCloseClick={() => setActiveInfo(null)}>
                  <div style={{ minWidth: 140 }}>
                    <div style={{ fontWeight: 700, marginBottom: 4 }}>{activeInfo.name}</div>
                    <div style={{ color: "#818D93", fontSize: 12 }}>
                      {activeInfo.pos.lat.toFixed(5)}, {activeInfo.pos.lng.toFixed(5)}
                    </div>
                    <button className="um-btn small" style={{ marginTop: 8 }}
                      onClick={() => fitToPath(activePath)}>Zoom to track</button>
                  </div>
                </InfoWindowF>
              )}

              {activePath && (
                <PolylineF path={activePath} options={{ strokeColor: "#FCAA30", strokeOpacity: 0.95, strokeWeight: 4 }} />
              )}
            </GoogleMap>
          ) : (
            <div className="um-loading">Loading map…</div>
          )}
        </div>
      </div>
    </div>
  );
}
